import { api, throwApiError } from "../../../api/api";

/** Escala da loja — fundação: feriados, horário e turnos da loja, preferências e lotação. */

export type DpHolidayScope = "NACIONAL" | "ESTADUAL" | "MUNICIPAL";
export type StoreShiftCode = "MANHA" | "NOITE";
export type DpShiftPreference = "MANHA" | "NOITE" | "ROTATIVO";
export type DpScheduleType = "SEIS_POR_UM" | "CINCO_POR_DOIS";

export const SCHEDULE_TYPE_LABELS: Record<DpScheduleType, string> = {
  SEIS_POR_UM: "6x1 (uma folga por semana)",
  CINCO_POR_DOIS: "5x2 (duas folgas por semana)",
};
export const SCHEDULE_TYPE_SHORT: Record<DpScheduleType, string> = { SEIS_POR_UM: "6x1", CINCO_POR_DOIS: "5x2" };

export const HOLIDAY_SCOPE_LABELS: Record<DpHolidayScope, string> = {
  NACIONAL: "Nacional",
  ESTADUAL: "Estadual",
  MUNICIPAL: "Municipal",
};

export const SHIFT_LABELS: Record<StoreShiftCode, string> = { MANHA: "Manhã", NOITE: "Noite" };

export const SHIFT_PREFERENCE_LABELS: Record<DpShiftPreference, string> = {
  MANHA: "Manhã",
  NOITE: "Noite",
  ROTATIVO: "Rotativo (manhã e noite)",
};

export const WEEKDAY_LABELS = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
export const WEEKDAY_SHORT = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

/** Minutos desde a meia-noite ↔ "HH:MM" (input type="time"). */
export const minutesToTime = (minutes?: number | null): string => {
  if (minutes === null || minutes === undefined) return "";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

export const timeToMinutes = (value: string): number | null => {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
};

export type DpHoliday = {
  id: string;
  date: string;
  name: string;
  scope: DpHolidayScope;
  state: string | null;
  storeId: string | null;
  store: { id: string; nomeFantasia: string } | null;
  /** Nulo = carregado pelo sistema. */
  createdById: string | null;
};

export type CreateHolidayPayload = {
  date: string;
  name: string;
  scope: DpHolidayScope;
  state?: string;
  storeId?: string;
};

export type StoreOpeningHour = { weekday: number; isOpen: boolean; openMinute: number | null; closeMinute: number | null };

export type StoreShift = {
  code: StoreShiftCode;
  startMinute: number;
  endMinute: number;
  breakMinutes: number;
  /** Sem uso: a necessidade do turno vem do hora x homem. */
  minStaff?: number;
  isActive: boolean;
};

export type StoreScheduleConfig = {
  storeId: string;
  storeName: string;
  estado: string | null;
  configured: boolean;
  opensOnHolidays: boolean;
  sundayOffEveryWeeks: number;
  /** Dias da semana (0 = domingo) em que ninguém folga. */
  noDayOffWeekdays: number[];
  updatedAt: string | null;
  openingHours: StoreOpeningHour[];
  shifts: StoreShift[];
  /** Posição da loja: raio da avaliação de loja e aviso "fora da loja" das marcações antigas. */
  location: StoreLocation | null;
};

export type StoreLocation = { latitude: number; longitude: number; radiusMeters: number };

export type SaveStoreScheduleConfigPayload = Pick<
  StoreScheduleConfig,
  "opensOnHolidays" | "sundayOffEveryWeeks" | "noDayOffWeekdays" | "openingHours" | "shifts"
> & { location?: StoreLocation | null };

export type StoreScheduleSummary = {
  withoutStore: number;
  stores: Array<{
    storeId: string;
    storeName: string;
    estado: string | null;
    configured: boolean;
    openWeekdays: number[];
    staffCount: number;
    /** Tem posição cadastrada para o ponto. */
    hasLocation: boolean;
  }>;
};

export type DpSchedulePreference = {
  employeeId: string;
  store: { id: string; nomeFantasia: string } | null;
  hasActiveContract: boolean;
  isDefault: boolean;
  shift: DpShiftPreference;
  /** Dias em que a loja do colaborador não dá folga. */
  noDayOffWeekdays: number[];
  fixedDayOff: number | null;
  notes: string | null;
  updatedAt: string | null;
  updatedBy: { id: string; nomeCompleto: string } | null;
};

export type BulkStoreAssignmentResult = {
  storeId: string;
  assigned: string[];
  skipped: Array<{ employeeId: string; reason: string }>;
};

const auth = (token: string) => {
  const raw = token?.startsWith("Bearer ") ? token.slice(7) : token;
  return { headers: { Authorization: `Bearer ${raw}` } };
};

async function call<T>(request: Promise<{ data: T }>, message: string): Promise<T> {
  try {
    const { data } = await request;
    return data;
  } catch (error) {
    throwApiError(error, message);
    throw error;
  }
}

// ── Feriados ────────────────────────────────────────────────────────────────

export const listHolidaysApi = (token: string, year: number, storeId?: string) =>
  call<DpHoliday[]>(api.get("/dp/holidays", { ...auth(token), params: { year, storeId } }), "Erro ao listar os feriados.");

export const createHolidayApi = (token: string, payload: CreateHolidayPayload) =>
  call<DpHoliday>(api.post("/dp/holidays", payload, auth(token)), "Erro ao cadastrar o feriado.");

export const deleteHolidayApi = (token: string, id: string) =>
  call<void>(api.delete(`/dp/holidays/${id}`, auth(token)), "Erro ao excluir o feriado.");

export const seedNationalHolidaysApi = (token: string, year: number) =>
  call<{ year: number; created: number; skipped: number; names: string[] }>(
    api.post(`/dp/holidays/seed/${year}`, {}, auth(token)),
    "Erro ao carregar os feriados nacionais.",
  );

// ── Horário e turnos da loja ────────────────────────────────────────────────

export const getStoreScheduleSummaryApi = (token: string) =>
  call<StoreScheduleSummary>(api.get("/stores/schedule-config/summary", auth(token)), "Erro ao carregar as lojas.");

export const getStoreScheduleConfigApi = (token: string, storeId: string) =>
  call<StoreScheduleConfig>(api.get(`/stores/${storeId}/schedule-config`, auth(token)), "Erro ao carregar o horário da loja.");

export const saveStoreScheduleConfigApi = (token: string, storeId: string, payload: SaveStoreScheduleConfigPayload) =>
  call<StoreScheduleConfig>(api.put(`/stores/${storeId}/schedule-config`, payload, auth(token)), "Erro ao salvar o horário da loja.");

/** Só a posição da loja (ficha da loja, aba Posição); `null` remove. Não mexe no horário. */
export const saveStoreLocationApi = (token: string, storeId: string, location: StoreLocation | null) =>
  call<StoreLocation | null>(api.put(`/stores/${storeId}/location`, { location }, auth(token)), "Erro ao salvar a posição da loja.");

/** Até onde a busca pelo CEP chegou: quanto menos preciso, mais conferir no mapa. */
export type CepPrecision = "NUMERO" | "RUA" | "BAIRRO" | "CIDADE";
export type CepLocation = { cep: string; address: string; latitude: number; longitude: number; precision: CepPrecision; found: string };

/** Latitude e longitude pelo CEP e número. Não grava: a tela preenche e o usuário salva. */
export const locateStoreByCepApi = (token: string, cep: string, numero?: string) =>
  call<CepLocation>(
    api.get("/stores/location/by-cep", { ...auth(token), params: { cep, ...(numero ? { numero } : {}) } }),
    "Erro ao buscar o CEP.",
  );

// ── Lotação e preferências ──────────────────────────────────────────────────

export const bulkAssignStoreApi = (token: string, payload: { storeId: string; employeeIds: string[] }) =>
  call<BulkStoreAssignmentResult>(api.post("/dp/store-assignments", payload, auth(token)), "Erro ao lotar os colaboradores.");

export const unassignStoreApi = (token: string, payload: { storeId: string; employeeIds: string[] }) =>
  call<BulkStoreAssignmentResult>(api.post("/dp/store-assignments/remove", payload, auth(token)), "Erro ao retirar os colaboradores da loja.");

export const getSchedulePreferenceApi = (token: string, employeeId: string) =>
  call<DpSchedulePreference>(api.get(`/dp/employees/${employeeId}/schedule-preference`, auth(token)), "Erro ao carregar a escala do colaborador.");

export const saveSchedulePreferenceApi = (
  token: string,
  employeeId: string,
  payload: { shift: DpShiftPreference; fixedDayOff: number | null; notes?: string },
) =>
  call<DpSchedulePreference>(
    api.put(`/dp/employees/${employeeId}/schedule-preference`, payload, auth(token)),
    "Erro ao salvar a escala do colaborador.",
  );

// ── Escala mensal da loja ───────────────────────────────────────────────────

export type DpRosterDayKind = "TRABALHO" | "FOLGA" | "FERIADO" | "FERIAS" | "AFASTAMENTO" | "LOJA_FECHADA";
export type DpRosterStatus = "RASCUNHO" | "PUBLICADO";
export type DpRosterChangeStatus = "PENDENTE" | "APROVADO" | "REJEITADO" | "CANCELADO";

export const ROSTER_KIND_LABELS: Record<DpRosterDayKind, string> = {
  TRABALHO: "Trabalho",
  FOLGA: "Folga",
  FERIADO: "Feriado",
  FERIAS: "Férias",
  AFASTAMENTO: "Afastamento",
  LOJA_FECHADA: "Loja fechada",
};

export const ROSTER_CHANGE_STATUS_LABELS: Record<DpRosterChangeStatus, string> = {
  PENDENTE: "Pendente",
  APROVADO: "Aprovado",
  REJEITADO: "Recusado",
  CANCELADO: "Cancelado",
};

export type DpRosterDay = {
  employeeId: string;
  date: string;
  kind: DpRosterDayKind;
  shift: StoreShiftCode | null;
  startMinute: number | null;
  endMinute: number | null;
  pinned: boolean;
};

export type DpRosterWarning =
  | { code: "COBERTURA_ABAIXO_DO_MINIMO"; date: string; shift: StoreShiftCode; missing: number }
  | {
      code:
        | "MAIS_DE_6_DIAS_SEGUIDOS"
        | "SEM_DESCANSO_NA_SEMANA"
        | "DOMINGO_SEM_FOLGA"
        | "SEM_DOMINGO_NO_MES"
        | "FOLGA_EM_DIA_BLOQUEADO"
        | "FOLGAS_CONSECUTIVAS"
        | "TRABALHO_SEM_TURNO";
      employeeId: string;
      employeeName: string;
      date: string;
    }
  | { code: "LOJA_SEM_TURNO" };

export type DpRoster = {
  id: string;
  storeId: string;
  storeName: string;
  year: number;
  month: number;
  status: DpRosterStatus;
  /** Tipo de escala da loja no mês (o da última sugestão). */
  scheduleType: DpScheduleType;
  suggestedAt: string | null;
  publishedAt: string | null;
  payrollClosed: boolean;
  storeConfigured: boolean;
  openingHours: StoreOpeningHour[];
  shifts: StoreShift[];
  sundayOffEveryWeeks: number;
  opensOnHolidays: boolean;
  holidays: string[];
  /** Pedidos por hora por pessoa usados no cálculo (o gravado na última sugestão ou o da Administração). */
  productivity: number;
  /** Produtividade vigente na Administração do sistema (vale na próxima sugestão). */
  currentProductivity: number;
  volumes: DpRosterVolume[];
  /** Pessoas necessárias por dia e turno (hora x homem); só os dias com volume. */
  demand: Array<{ date: string } & Partial<Record<StoreShiftCode, number>>>;
  employees: Array<{
    employeeId: string;
    contractId: string;
    name: string;
    shift: DpShiftPreference;
    fixedDayOff: number | null;
    from: string;
    to: string;
  }>;
  days: DpRosterDay[];
  warnings: DpRosterWarning[];
  pendingChangeRequests: number;
};

export type DpRosterVolume = { date: string; volume: number; morningPct: number };

export type DpRosterCellUpdate = {
  employeeId: string;
  date: string;
  kind?: "TRABALHO" | "FOLGA";
  shift?: StoreShiftCode;
  release?: boolean;
};

export type DpRosterChangeRequest = {
  id: string;
  fromDate: string;
  toDate: string;
  reason: string | null;
  status: DpRosterChangeStatus;
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
  employee: { id: string; fullName: string; socialName: string | null };
  roster: { id: string; year: number; month: number; store: { id: string; nomeFantasia: string } };
  decidedBy: { id: string; nomeCompleto: string } | null;
};

export const openRosterApi = (token: string, storeId: string, year: number, month: number) =>
  call<DpRoster>(api.get("/dp/rosters", { ...auth(token), params: { storeId, year, month } }), "Erro ao abrir a escala.");

export const suggestRosterApi = (token: string, id: string, scheduleType: DpScheduleType) =>
  call<DpRoster>(api.post(`/dp/rosters/${id}/suggest`, { scheduleType }, auth(token)), "Erro ao sugerir a escala.");

export const updateRosterDaysApi = (token: string, id: string, cells: DpRosterCellUpdate[]) =>
  call<DpRoster>(api.put(`/dp/rosters/${id}/days`, { cells }, auth(token)), "Erro ao salvar a escala.");

export const saveRosterVolumesApi = (token: string, id: string, days: DpRosterVolume[]) =>
  call<DpRoster>(api.put(`/dp/rosters/${id}/volumes`, { days }, auth(token)), "Erro ao salvar os volumes.");

export const publishRosterApi = (token: string, id: string) =>
  call<DpRoster>(api.post(`/dp/rosters/${id}/publish`, {}, auth(token)), "Erro ao publicar a escala.");

export const unpublishRosterApi = (token: string, id: string) =>
  call<DpRoster>(api.post(`/dp/rosters/${id}/unpublish`, {}, auth(token)), "Erro ao voltar a escala para rascunho.");

export const listRosterChangeRequestsApi = (token: string, params: { status?: DpRosterChangeStatus; storeId?: string } = {}) =>
  call<DpRosterChangeRequest[]>(api.get("/dp/roster-change-requests", { ...auth(token), params }), "Erro ao listar os pedidos de troca.");

export const approveRosterChangeApi = (token: string, id: string, note?: string) =>
  call<unknown>(api.post(`/dp/roster-change-requests/${id}/approve`, { note }, auth(token)), "Erro ao aprovar a troca.");

export const rejectRosterChangeApi = (token: string, id: string, note?: string) =>
  call<unknown>(api.post(`/dp/roster-change-requests/${id}/reject`, { note }, auth(token)), "Erro ao recusar a troca.");

/** Texto curto de um aviso da escala, para a lista da tela. */
export const describeRosterWarning = (w: DpRosterWarning): string => {
  const date = "date" in w ? new Date(`${w.date}T00:00:00Z`).toLocaleDateString("pt-BR", { timeZone: "UTC" }) : "";
  switch (w.code) {
    case "COBERTURA_ABAIXO_DO_MINIMO":
      return `${date}: faltam ${w.missing} no turno da ${SHIFT_LABELS[w.shift].toLowerCase()} para o volume previsto.`;
    case "MAIS_DE_6_DIAS_SEGUIDOS":
      return `${w.employeeName}: mais de 6 dias seguidos de trabalho até ${date}.`;
    case "SEM_DESCANSO_NA_SEMANA":
      return `${w.employeeName}: semana de ${date} com menos folgas do que a escala dele (6x1 ou 5x2) pede.`;
    case "SEM_DOMINGO_NO_MES":
      return `${w.employeeName}: nenhum domingo de folga no mês.`;
    case "FOLGA_EM_DIA_BLOQUEADO":
      return `${w.employeeName}: folga em ${date}, dia em que a loja não dá folga.`;
    case "FOLGAS_CONSECUTIVAS":
      return `${w.employeeName}: folgas seguidas a partir de ${date}.`;
    case "DOMINGO_SEM_FOLGA":
      return `${w.employeeName}: sem domingo de folga no prazo (até ${date}).`;
    case "TRABALHO_SEM_TURNO":
      return `${w.employeeName}: dia de trabalho sem turno em ${date}.`;
    case "LOJA_SEM_TURNO":
      return "A loja não tem turno ativo: configure em Cadastros → Lojas e turnos.";
    default:
      return "";
  }
};
